import { type Editor, Node } from '@tiptap/core';
import { type EditorType } from '../editor-type';
import {
  createSectionInputRules,
  type SectionInputRuleCreationMethod,
} from './section-input-rule';
import {
  handleSectionHeadingEnter,
  handleSectionShiftTab,
  handleSectionTab,
} from './section-keyboard-navigation';
import { sectionMarkdownSpec } from './section-markdown';
import { sectionNodeSchemaSpec } from './section-node-schema';

export interface MarkdownSectionNodeOptions {
  /**
   * Host-supplied dispatch for the `# ` input rule's section creation
   * (OpenSpec `monorepo-kit-editor` task 4.2). The kernel reports only its own
   * mechanism (`heading_shortcut`) and the live editor; the app adapter wired
   * by the extension kit resolves `editor_context` from this node's storage at
   * emit time and owns the product analytics vocabulary. `null` (the default)
   * means no telemetry — package-internal headless/conversion editors never
   * run the typing pipeline, so nothing is lost there.
   */
  onSectionCreated:
    | ((creationMethod: SectionInputRuleCreationMethod, editor: Editor) => void)
    | null;
}

export interface MarkdownSectionNodeStorage {
  /**
   * The host editor's type (note vs document), stamped by
   * `useMarkdownNativeEditor`. Read by the editor-driven creation paths — the
   * `# ` input rule, the slash command and the convert-to-section bubble
   * command — to report `editor_context` on the `section_created` analytics
   * event without React context. Twin of the vMarkdown `SectionNodeStorage`.
   */
  editorType: EditorType | undefined;
}

// React-free markdown-native section kernel (OpenSpec monorepo-kit-editor task
// 4.1): the shared schema, the markdown spec, storage, the `# ` → section input
// rules, and the shared section keyboard navigation (Enter into body,
// Tab/Shift-Tab between heading and body / sections). The interactive React
// node view stays app-side in `section-node.ts` (design D4) — headless
// round-trip/derivation editors serialize via the markdown spec and never
// render a view. This base is the 5.2 `/core` extraction candidate; that move
// also rewrites the remaining `@tiptap/react` re-export imports here and in the
// shared leaves to `@tiptap/core` (same physical module, singleton-asserted).
export const MarkdownSectionNodeBase = Node.create<
  MarkdownSectionNodeOptions,
  MarkdownSectionNodeStorage
>({
  ...sectionNodeSchemaSpec,
  ...sectionMarkdownSpec,

  addOptions() {
    return { onSectionCreated: null };
  },

  addStorage(): MarkdownSectionNodeStorage {
    return { editorType: undefined };
  },

  addInputRules() {
    // The rules run outside React and carry no analytics themselves; the host
    // injects the dispatch via `onSectionCreated` (the extension kit wires the
    // app-side adapter, which reads `editor_context` from this node's storage
    // at emit time).
    return createSectionInputRules((creationMethod) => {
      this.options.onSectionCreated?.(creationMethod, this.editor);
    });
  },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => handleSectionHeadingEnter(editor),
      Tab: ({ editor }) => handleSectionTab(editor),
      'Shift-Tab': ({ editor }) => handleSectionShiftTab(editor),
    };
  },
});
